import styles from "./Funnel.module.css";

/**
 * Funnel — the "but my computer already ships with Notes and a
 * Calculator" trust beat. Four blocks on a single vertical axis:
 *
 *   1. HEADER — title (the question) + lede (the specifics).
 *   2. EXHIBIT — rendered as a faux store receipt: monospace
 *      throughout, store-name header, dotted leaders between each
 *      FAANG free app and the upsell it hides, faux barcode at the
 *      foot.
 *   3. ATLAS DECLARATION — display-sized editorial answer. No card
 *      chrome, no border, no box — pure typography on the cream
 *      surface, deliberately different in vocabulary from the
 *      bordered exhibit above. The teal accent on "Nothing else for
 *      sale." mirrors-and-answers the vermillion subtitle.
 *   4. SIGNATURE — short signed-off line ("Made for everyone. Sold
 *      by no one.") closes the section on the personal-trust beat.
 */
const UPSELL_ROWS: ReadonlyArray<{ from: string; to: string }> = [
  { from: "Apple Notes", to: "iCloud+" },
  { from: "iTunes", to: "Apple Music" },
  { from: "Google Keep", to: "Google One" },
  { from: "Outlook Mail", to: "Microsoft 365" },
];



export function Funnel() {
  return (
    <section
      id="funnel"
      className={styles.section}
      aria-labelledby="funnel-title"
    >
      <div className={`atlasContainer ${styles.inner}`}>
        <header className={styles.header}>
          <h2 id="funnel-title" className={styles.title}>
            But my computer
            <br />
            <span className={styles.titleUnderline}>
              already has these apps?
            </span>
          </h2>
          <p className={styles.lede}>
            Fortune 500 companies ship notes apps, music players, mail
            clients — because each one is a doorway into a paid tier.
          </p>
        </header>

        {/* Receipt. Off-white paper card, monospace throughout, a
            store-name header, dotted leaders between item names and
            the upsell they hide, a faux barcode at the bottom. The
            outer wrapper carries the hard-offset drop shadow; the
            inner <article> carries borders + paper background. */}
        <div className={styles.exhibitWrap}>
          <article
            className={styles.exhibit}
            aria-label="Receipt — big-tech free apps and the paid tiers they upsell into"
          >
          <header className={styles.exhibitHeader}>
            <h3 className={styles.exhibitStore}>Big Tech, LLC.</h3>
            <p className={styles.exhibitSubhead}>
              Apps that sell other things
            </p>
          </header>

          <hr className={styles.exhibitRule} aria-hidden />

          <div className={styles.exhibitCols}>
            <span>Item</span>
            <span>Upsell</span>
          </div>

          <hr className={styles.exhibitRule} aria-hidden />

          <ul className={styles.exhibitList}>
            {UPSELL_ROWS.map((r) => (
              <li key={r.from} className={styles.exhibitItem}>
                <span className={styles.exhibitFree}>{r.from}</span>
                <span className={styles.exhibitDots} aria-hidden />
                <span className={styles.exhibitPaid}>{r.to}</span>
              </li>
            ))}
          </ul>

          <hr className={styles.exhibitRule} aria-hidden />

          <p className={styles.exhibitCaption}>
            Free at the door.
            <br />
            The room costs money.
          </p>

          <hr className={styles.exhibitRule} aria-hidden />

          <div className={styles.exhibitBarcode} aria-hidden />
          <p className={styles.exhibitFooter}>
            thank you for your data
          </p>
          </article>
        </div>

        {/* Atlas declaration — no chrome, just type. Display-sized
            headline answers the exhibit above; body inventories what
            isn't being sold; signature signs off in a single ink
            rule + mono line. */}
        <div className={styles.atlas}>
          <h3 className={styles.atlasHeadline}>
            Atlas Desk{" "}
            <span className={styles.atlasHeadlineEquals}>=</span>{" "}
            <span className={styles.atlasHeadlineAccent}>Just apps.</span>
          </h3>
          <p className={styles.atlasBody}>
            No paid tier. No storage upsell. No AI credits, no
            subscription, no account to make. The notes app is just a
            notes app. The music player, well, it plays music.
          </p>
          <div className={styles.atlasSignatureRow}>
            <span className={styles.atlasSignatureRule} aria-hidden />
            <span className={styles.atlasSignature}>
              Made for everyone. Sold by no one.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
